import { useCallback, useEffect, useRef, useState } from 'react'

export type RecorderStatus = 'idle' | 'recording' | 'error'

/**
 * Audio recording hook for voice notes (mobile-friendly).
 *
 * - Requires HTTPS or localhost (getUserMedia restriction).
 * - Records audio/webm where supported, falling back to audio/mp4 (iOS Safari).
 */
export function useAudioRecorder() {
  const [status, setStatus] = useState<RecorderStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stopResolveRef = useRef<((blob: Blob | null) => void) | null>(null)

  const isSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'

  const cleanupStream = (recorder: MediaRecorder | null) => {
    recorder?.stream.getTracks().forEach((track) => track.stop())
  }

  const start = useCallback(async () => {
    setError(null)
    if (!isSupported) {
      setError('La grabación de audio no está disponible en este navegador (se necesita HTTPS).')
      setStatus('error')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      // iOS Safari doesn't support audio/webm — fall back to audio/mp4
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : ''

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        cleanupStream(recorder)
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
        stopResolveRef.current?.(blob.size > 0 ? blob : null)
        stopResolveRef.current = null
      }

      mediaRecorderRef.current = recorder
      recorder.start()
      setElapsedSeconds(0)
      timerRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000)
      setStatus('recording')
    } catch (err) {
      const e = err as DOMException
      if (e.name === 'NotAllowedError') {
        setError('Permití el acceso al micrófono en tu navegador para grabar notas de voz.')
      } else if (e.name === 'NotFoundError') {
        setError('No se encontró un micrófono en este dispositivo.')
      } else {
        setError('No se pudo iniciar la grabación: ' + e.message)
      }
      setStatus('error')
    }
  }, [isSupported])

  /** Stop recording and resolve with the recorded audio blob (or null). */
  const stop = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      if (!recorder || recorder.state === 'inactive') {
        setStatus('idle')
        resolve(null)
        return
      }
      stopResolveRef.current = resolve
      recorder.stop()
      setStatus('idle')
    })
  }, [])

  /** Cancel recording, discarding the audio. */
  const cancel = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    stopResolveRef.current = null
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = () => cleanupStream(recorder)
      recorder.stop()
    }
    setStatus('idle')
  }, [])

  // Release the mic if the component unmounts mid-recording
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      const recorder = mediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = () => cleanupStream(recorder)
        recorder.stop()
      }
    }
  }, [])

  return { status, error, elapsedSeconds, isSupported, start, stop, cancel }
}
